import React, { useRef, useState } from "react";
import styled from "styled-components";
// Import ToastContainer and toast
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Import toast styles

// Assets
import ContactImg3 from "../../assets/img/contact 1.png";
import GoogleMap from '../../components/map/googlemap.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s-]{6,17}$/;

function validateContactForm(data) {
  const errors = {};

  if (!data.from_name?.trim()) errors.from_name = "Please enter your name.";

  if (!data.reply_to?.trim()) errors.reply_to = "Please enter your email.";
  else if (!EMAIL_RE.test(data.reply_to.trim())) errors.reply_to = "Please enter a valid email address.";

  if (!data.phone_number?.trim()) errors.phone_number = "Please enter your phone number.";
  else if (!PHONE_RE.test(data.phone_number.trim())) errors.phone_number = "Please enter a valid phone number.";

  if (!data.subject?.trim()) errors.subject = "Please enter a subject.";

  if (!data.message?.trim()) errors.message = "Please enter a message.";
  else if (data.message.trim().length < 10) errors.message = "Message should be at least 10 characters.";

  return errors;
}

export default function Contact() {
  const form = useRef(); // Keeps a reference to the form
  const [errors, setErrors] = useState({});

  const clearError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const sendEmail = async (e) => {
    e.preventDefault(); // Prevent the form from submitting normally (refreshing page)

    // Using FormData to extract data from form fields
    const formData = new FormData(form.current);
    const data = Object.fromEntries(formData); // Converts FormData to a regular JS object

    const validationErrors = validateContactForm(data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setErrors({});

    try {
      // Send a POST request to the backend
      const response = await fetch('https://www.chem-labs.com/api/contact/finechek', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', // Ensure the request is JSON
        },
        body: JSON.stringify(data), // Convert the data object to JSON
      });

      // Handle the response
      if (response.ok) {
        console.log('Message Sent Successfully!');
        toast.success('Message sent successfully!'); // Show success toast
        
        // Reset the form fields
        form.current.reset(); // Reset the form fields
      } else {
        console.log('Failed to Send Message');
        toast.error('Failed to send message.'); // Show error toast
        form.current.reset(); // Reset the form fields
      }
    } catch (error) {
      console.log('Error:', error);
      toast.error('An error occurred while sending the message.'); // Show error toast
      form.current.reset(); // Reset the form fields
    }
  };

  return (
    <Wrapper id="contact">
      <div className="lightBg">
        <div className="container">
          <HeaderInfo>
            <h1 className="font40 extraBold">We're Just a Message Away!</h1>
          </HeaderInfo>
          <div className="row" style={{ paddingBottom: "30px" }}>
            <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6">
              <Form ref={form} onSubmit={sendEmail} noValidate>
                <Field>
                  <label className="font13 semiBold">Full Name</label>
                  <input
                    type="text"
                    name="from_name"
                    className={errors.from_name ? "invalid" : ""}
                    onChange={() => clearError("from_name")}
                    aria-invalid={Boolean(errors.from_name)}
                  />
                  {errors.from_name && <ErrorText>{errors.from_name}</ErrorText>}
                </Field>

                <Field>
                  <label className="font13 semiBold">Email</label>
                  <input
                    type="email"
                    name="reply_to"
                    className={errors.reply_to ? "invalid" : ""}
                    onChange={() => clearError("reply_to")}
                    aria-invalid={Boolean(errors.reply_to)}
                  />
                  {errors.reply_to && <ErrorText>{errors.reply_to}</ErrorText>}
                </Field>

                <Field>
                  <label className="font13 semiBold">Phone Number</label>
                  <input
                    type="text"
                    name="phone_number"
                    className={errors.phone_number ? "invalid" : ""}
                    onChange={() => clearError("phone_number")}
                    aria-invalid={Boolean(errors.phone_number)}
                  />
                  {errors.phone_number && <ErrorText>{errors.phone_number}</ErrorText>}
                </Field>

                <Field>
                  <label className="font13 semiBold">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    className={errors.subject ? "invalid" : ""}
                    onChange={() => clearError("subject")}
                    aria-invalid={Boolean(errors.subject)}
                  />
                  {errors.subject && <ErrorText>{errors.subject}</ErrorText>}
                </Field>

                <Field>
                  <label className="font13 semiBold">Message</label>
                  <textarea
                    rows="4"
                    name="message"
                    className={errors.message ? "invalid" : ""}
                    onChange={() => clearError("message")}
                    aria-invalid={Boolean(errors.message)}
                  />
                  {errors.message && <ErrorText>{errors.message}</ErrorText>}
                </Field>

                <SumbitWrapper className="flex">
                  <ButtonInput
                    type="submit"
                    value="Send Message"
                    className="pointer animate radius8"
                    style={{ maxWidth: "220px" }}
                  />
                </SumbitWrapper>
              </Form>
            </div>
            <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 flex">
              <div
                style={{
                  marginTop: "100px",
                  width: "100%",
                  maxWidth: "500px",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                <img
                  src={ContactImg3}
                  alt="office"
                  className="radius6"
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <GoogleMap />
      {/* Add ToastContainer to render the toast messages */}
      <ToastContainer 
        position="top-right" // Position of the toast messages
        autoClose={5000} // Duration for which the toast should be visible
        hideProgressBar={false} // Show progress bar
        newestOnTop={false} // Newest toast appears at the bottom
        closeOnClick 
        rtl={false} 
        pauseOnFocusLoss 
        draggable 
        pauseOnHover 
      />
    </Wrapper>
  );
}

const Wrapper = styled.section`
  width: 100%;
`;

const Form = styled.form`
  padding: 40px 0 30px 0;
  input,
  textarea {
    width: 100%;
    box-sizing: border-box;
    background-color: #fff;
    border: 1px solid #e2e2e2;
    outline: none;
    box-shadow: none;
    border-radius: 8px;
    padding: 12px 16px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  textarea {
    min-height: 110px;
    resize: vertical;
  }

  input:focus,
  textarea:focus {
    outline: none;
    border-color: rgb(119, 31, 115);
    box-shadow: 0 0 0 3px rgba(119, 31, 115, 0.15);
  }

  input.invalid,
  textarea.invalid {
    border-color: #e53e3e;
  }
  input.invalid:focus,
  textarea.invalid:focus {
    box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.15);
  }

  input:-webkit-autofill,
  textarea:-webkit-autofill {
    -webkit-box-shadow: 0 0 0px 1000px white inset; /* Replace 'white' with your background color */
    box-shadow: 0 0 0px 1000px white inset;
    -webkit-text-fill-color: black; /* Replace 'black' with your desired text color */
    transition: background-color 5000s ease-in-out 0s; /* Keep the autofill color for a long time */
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 22px;
`;

const ErrorText = styled.span`
  font-size: 0.75rem;
  color: #e53e3e;
`;

const ButtonInput = styled.input`
  border: 1px solid rgb(119, 31, 115);
  background-color: rgb(119, 31, 115) !important;
  width: 100%;
  padding: 24px;
  outline: none;
  color: #fff;
`;

const SumbitWrapper = styled.div`
  @media (max-width: 991px) {
    width: 100%;
    margin-bottom: 50px;
  }
`;

const HeaderInfo = styled.div`
  padding: 70px 0 30px 0;
  @media (max-width: 860px) {
    text-align: center;
  }
`;
